import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';
import { highlight } from 'highlight.js';
import _ from 'lodash';

import { DEFAULT_INITIAL_SUPPORT_STEP, FEATURES, INBOX_TYPES, routes } from 'src/app/constants';
import { Customer, Widget, InboundEmailRule } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';
import { WidgetsService } from 'src/services/widgets.service';

const DEFAULT_COLOR = '#206cdf';
const DEFAULT_REPLY_TIME = 'in_a_few_hours';
const DEFAULT_BUBBLE_STYLE = 'standard';
const DEFAULT_BUBBLE_POSITION = 'right';

function getCodeForWidget(widgetId: string) {
  return `<script>
  (function (w, d, t) {
    w.$unthreadInAppChatSettings = {
      baseUrl: '${window.location.origin}',
      widgetId: '${widgetId}'
    };

    var u = function() {
      u.c(arguments);
    };
    u.q = [];
    u.c = function(args) {
      u.q.push(args);
    };

    if (!w.$unthread) {
      w.$unthread = {};
    }

    w.$unthread.inAppChat = u;

    var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
    g.src = w.$unthreadInAppChatSettings.baseUrl + '/widget/js/wrapper.js';
    g.defer = true;
    g.async = true;
    s.parentNode.insertBefore(g, s);
  })(window, document, 'script');
</script>`;
}

function getCodeForHelpCenter(helpCenterId: string) {
  return `<script>
  (function (w, d, t) {
    w.$unthreadHelpCenterSettings = {
      baseUrl: '${window.location.origin}',
      helpCenterId: '${helpCenterId}'
    };

    var u = function() {
      u.c(arguments);
    };
    u.q = [];
    u.c = function(args) {
      u.q.push(args);
    };

    if (!w.$unthread) {
      w.$unthread = {};
    }

    w.$unthread.helpCenter = u;

    var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
    g.src = w.$unthreadHelpCenterSettings.baseUrl + '/widget/js/hc-wrapper.js';
    g.defer = true;
    g.async = true;
    s.parentNode.insertBefore(g, s);
  })(window, document, 'script');
</script>`;
}

@Component({
  selector: 'app-admin-widget',
  templateUrl: './admin-widget.component.html',
  styleUrls: ['./admin-widget.component.scss'],
})
export class AdminWidgetComponent implements OnInit {
  verificationSecret: string;
  isLoading = false;
  widgets: Partial<Widget>[] | null;
  supportSteps: any[] = [DEFAULT_INITIAL_SUPPORT_STEP];
  featureEnabled = true;
  code?: string;
  verificationSecretShown = false;
  hasWidgets = false;
  leftTab: 'preview' | 'code' = 'preview';
  inboundEmailRules: Partial<InboundEmailRule>[] | null;
  widgetToEdit: Partial<Widget>;
  showConfigModal = false;
  isReplyPublic: boolean;
  isDarkModePreview: boolean;
  projectId: string;
  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private navController: NavController,
    private customersService: CustomersService,
    private widgetsService: WidgetsService,
    private msgService: MessageService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private route: ActivatedRoute,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    this.isLoading = true;
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.WIDGET);
    this.isReplyPublic = this.authService.tenant.defaultToPublicMessages;

    await Promise.all([this.getWidgets(), this.getInboundEmailRules()]);

    this.isLoading = false;
  }

  async getWidgets() {
    const result = await this.widgetsService.list({
      order: ['createdAt'],
      descending: false,
      where: [
        {
          field: 'projectId',
          operator: '==',
          value: this.projectId,
        },
      ],
    });

    this.widgets = result.data ?? null;
    this.hasWidgets = !!this.widgets.length;

    this.getCustomers();
  }

  async getInboundEmailRules() {
    const result = await this.inboundEmailRulesService.list({
      order: ['createdAt'],
      descending: false,
      where: [
        {
          field: 'projectId',
          operator: '==',
          value: this.projectId,
        },
      ],
    });

    this.inboundEmailRules = result.data ?? null;
  }

  getCodeHtmlRaw(widget: Partial<Widget>) {
    return widget.isFullPageView ? getCodeForHelpCenter(widget.id) : getCodeForWidget(widget.id);
  }

  getCodeHtml(widget: Partial<Widget>) {
    return highlight('html', this.getCodeHtmlRaw(widget)).value;
  }

  getIFrameHtmlRaw() {
    return '<iframe id="unthread_help_center" style="width: 100%; height: 100%; border: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 999999;"></iframe>';
  }

  getIFrameHtml() {
    return highlight('html', this.getIFrameHtmlRaw()).value;
  }

  copyIFrameHtml() {
    navigator.clipboard
      .writeText(this.getIFrameHtmlRaw())
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async getCustomers() {
    if (this.widgets?.length) {
      // It will be way easier to batch these, but we're movign them to postgres anyway so I'm not going to waste the time
      this.widgets.forEach(async (rule) => {
        const customer: Partial<Customer> = await this.customersService.get(rule.customerId);
        rule.name = customer.name;
        rule.supportSteps = customer.supportSteps;
      });
    }
  }

  addWidget() {
    this.widgetToEdit = {
      name: '',
      title: 'Hi there',
      tagline: 'Let us know if you have any questions',
      color: DEFAULT_COLOR,
      replyTime: DEFAULT_REPLY_TIME,
      bubbleStyle: DEFAULT_BUBBLE_STYLE,
      bubblePosition: DEFAULT_BUBBLE_POSITION,
      supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
      channelId: '',
      linkedInboundEmailRuleId: null,
      isFullPageView: false,
      ticketTypesEnabled: false,
      darkMode: 'light',
      projectId: this.projectId,
    };
    this.showConfigModal = true;
  }

  copyToClipboard(widget: Partial<Widget>) {
    navigator.clipboard
      .writeText(this.getCodeHtmlRaw(widget))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async save(widget: Partial<Widget>) {
    try {
      this.isLoading = true;
      const result = widget?.id
        ? await this.widgetsService.update(widget.id, widget)
        : await this.widgetsService.create(widget);
      this.isLoading = false;

      if (widget?.id) {
        this.dismissWidgetEditor();
      } else {
        widget.id = result.id;
        this.leftTab = 'code';
      }

      this.msgSrvc.show('✔︎ Saved');
    } catch (e) {
      this.isLoading = false;
      this.msgSrvc.showError(e);
    }
  }

  supportStepsDidChange(supportSteps: any[]) {
    this.widgetToEdit.supportSteps = supportSteps;
  }

  async delete(widget: Partial<Widget>) {
    if (!widget || !widget.id) {
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Disable chat widget',
      subHeader: 'Are you sure you want to disable the chat widget?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            this.isLoading = true;
            await this.widgetsService.delete(widget.id);
            this.dismissWidgetEditor();
            this.isLoading = false;
          },
        },
      ],
    });

    await alert.present();
  }

  toggleVerificationSecret() {
    this.verificationSecretShown = !this.verificationSecretShown;
  }

  showWidgetHelpdesk() {
    window.open('https://docs.unthread.io/in-app/chat-widget', '_blank');
  }

  editWidget(widget: Partial<Widget>) {
    this.widgetToEdit = widget;
    this.showConfigModal = true;
  }

  dismissWidgetEditor() {
    this.showConfigModal = false;
  }

  onWidgetChannelChanged(widget, slackChannel) {
    widget.channelId = slackChannel?.id ?? null;
    widget.slackChannel = slackChannel;

    if (widget.id) {
      this.save(widget);
    }
  }

  viewInbox(rule: Partial<Widget>) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.IN_APP}/${rule.id}`);
  }

  onDismissConfigModal() {
    this.getWidgets();
    this.showConfigModal = false;
  }

  autoresponderDidChange(widget: Partial<Widget>, autoresponder: any) {
    widget.autoresponder = autoresponder;
  }

  async isReplyPublicDidChange() {
    try {
      await this.authService.updateTenant({
        defaultToPublicMessages: this.isReplyPublic,
      });
      this.msgService.show('✔︎ Updated');
    } catch (err) {
      this.msgService.show(`Something went wrong: ${err.message}`);
    }
  }
}
